import { DiscoverTeamGetResponse } from "libs/apiClient"
import { ContentCell } from "./ContentCell";
import client from "libs/client";
import { UserPermissionsTable } from "./UserPermissionsTable";
import { AgeCategoryOptions, AllGenderOptions } from "./types";
import { DiscoverPlayersTable } from "./DiscoverPlayersTable";
import { useId, useState } from "react";
import { Link } from "react-router";
import { DiscoverGamesTable } from "features/discover/DiscoverGamesTable";
import { Tabs } from "features/discover/Tabs";
import { TeamTokenMovementsTable } from "features/discover/TeamTokenMovementsTable";
import { DiscoverSubscriptionsTable } from "features/discover/DiscoverSubscriptionsTable";
import { DiscoverTeamGroupsTable } from "features/discover/DiscoverTeamGroupsTable";

const CameraBrandOptions = [
	{ value: "trace", label: "Trace" },
	{ value: "veo", label: "Veo" },
	{ value: "pixellot", label: "Pixellot" },
	{ value: "hudl", label: "HUDL" },
	{ value: "reeplayer", label: "Reeplayer" },
	{ value: "ballertv", label: "BallerTv" },
]

export function DiscoverTeamDetail({ teamData }: { teamData: DiscoverTeamGetResponse }) {
	const [tab, setTab] = useState<string | null>("games");

	const contentProps = {
		mutation: client.patchDiscoverTeam.useMutation(),
		invalidationKey: 'discoverTeamDetail',
		id: teamData.id,
	};

	const { data: countries, isLoading: isCountriesLoading } = client.getDiscoverCountries.useQuery({});

	const permissions = teamData?.userPermissions || [];

	const teamProps = [
		{
			title: "Team name",
			field: "name",
			type: "string",
			value: teamData.name,
		},
		{
			title: "Clubhouse",
			type: "link",
			formatter: () => teamData.club && <Link to={`/discover/club/${teamData.club.id}`} className="button-link">{teamData.club.name}</Link>,
		},
		...(teamData.isDiscover ? [{
			type: "empty",
		}] : []),
		...(teamData.isDiscover ? [{
			type: "empty",
		}] : []),
		...(teamData.isDiscover ? [{
			title: "Country",
			field: "country",
			type: "list",
			value: teamData.country?.id,
			isSelectLoading: isCountriesLoading,
			options: countries?.data || [],
		}] : []),
		...(teamData.isDiscover ? [{
			title: "Region",
			field: "region",
			type: "string",
			value: teamData.region,
		}] : []),
		...(teamData.isDiscover ? [{
			title: "City",
			field: "city",
			type: "string",
			value: teamData.city,
		}] : []),
		...(teamData.isDiscover ? [{
			type: "empty",
		}] : []),
		{
			title: "Age category",
			field: "ageCategory",
			type: "list",
			value: teamData.ageCategory,
			options: AgeCategoryOptions,
		},
		{
			title: "Gender",
			field: "gender",
			type: "list",
			value: teamData.gender,
			options: AllGenderOptions,
		},
		...(teamData.isDiscover ? [{
			title: "Claimed number of players",
			field: "playersCount",
			type: "number",
			value: teamData.playersCount,
		}] : []),
		...(teamData.isDiscover ? [{
			title: "Camera brand",
			field: "cameraBrand",
			type: "list",
			value: teamData.cameraBrand,
			options: CameraBrandOptions,
		}] : []),
		...(teamData.isDiscover ? [{
			title: "Is discoverable",
			field: "isDiscoverable",
			type: "boolean",
			value: teamData.isDiscoverable,
		}] : []),
		...(teamData.isDiscover ? [{
			title: "Player access to team mate highlights",
			field: "hasPlayerAccessToMateHighlights",
			type: "boolean",
			value: teamData.hasPlayerAccessToMateHighlights,
		}] : []),
		...(teamData.isDiscover ? [{
			title: "Player access to metrics",
			field: "hasPlayerAccessToMetrics",
			type: "boolean",
			value: teamData.hasPlayerAccessToMetrics,
		}] : []),
		...(teamData.isDiscover ? [{
			title: "Player access to ratings",
			field: "hasPlayerAccessToRatings",
			type: "boolean",
			value: teamData.hasPlayerAccessToRatings,
		}] : []),
		...(!teamData.isDiscover ? [{
			title: "Team page",
			type: "link",
			formatter: () => <Link to={`/club/${teamData.club.id}/teams#${teamData.id}`} className="button-link" target="_blank">Partner ({teamData.id})</Link>,
		}] : []),
		...(!teamData.isDiscover ? [{
			title: "Player app disabled",
			field: "isPlayerAppDisabled",
			type: "boolean",
			formatter: () => <span className={teamData.isPlayerAppDisabled ? "false" : "true"}>{teamData.isPlayerAppDisabled ? "Disabled" : "Enabled"}</span>,
		}] : []),
	];

	const tabs = [
		{
			label: "Games",
			key: "games"
		}, {
			label: "Players",
			key: "players",
		}, {
			label: "Permissions",
			key: "permissions",
			disabled: permissions.length === 0
		}, {
			label: "Token movements",
			key: "tokenMovements",
			disabled: !teamData.tokenReadAccess
		}, {
			label: "Subscriptions",
			key: "subscriptions",
			disabled: !teamData.subscriptionReadAccess,
		}, {
			label: "Team groups",
			key: "teamGroups",
			disabled: !teamData.isDiscover || !teamData.teamGroupReadAccess,
		},
	];

	return (
		<>
			<div className="discover-card__title">Basic team data</div>
			<div className="discover-card">
				<div className="cell-grid">
					{teamProps.map((props) => {
						const key = useId();

						return <ContentCell key={key} {...contentProps} {...props} />
					})}
				</div>
			</div>

			<Tabs items={tabs} active={tab} onClick={(key) => setTab(key)} />

			<div className="discover-card discover-card--for-table" style={{ display: tab === "games" ? "block" : "none" }}>
				<DiscoverGamesTable defaultFilters={{
					anyTeamId: teamData.id,
				}} columnsVisibilityKey="columnsVisibilityTeam" />
			</div>

			<div className="discover-card" style={{ display: tab === "players" ? "block" : "none" }}>
				<DiscoverPlayersTable columnVisibility={{
					club: false,
					team: false,
					entityType: false,
				}} defaultFilters={{
					teamId: teamData.id,
				}} />
			</div>

			{tab === "permissions" && <div className="discover-card">
				<UserPermissionsTable userPermissions={teamData?.userPermissions || []} />
			</div>}

			{tab === "tokenMovements" && <div className="discover-card">
				<TeamTokenMovementsTable
					teamId={teamData.id}
					tokenBalance={teamData?.tokenBalance || 0}
					tokenMovements={teamData?.tokenMovements || []}
					tokenEditAccess={teamData.tokenEditAccess}
				/>
			</div>}

			{tab === "subscriptions" && <div className="discover-card">
				<DiscoverSubscriptionsTable teamId={teamData.id} subscriptionEditAccess={teamData.subscriptionEditAccess} />
			</div>}

			{tab === "teamGroups" && <div className="discover-card discover-card--for-table">
				<DiscoverTeamGroupsTable teamId={teamData.id} teamGroupEditAccess={teamData.teamGroupEditAccess} />
			</div>}
		</>
	)
}
